<script lang="ts" setup>
import { ChatSceneTargetType } from "@/enums/chatEnums";
import { useChatSession } from "@/stores/chatSession";
import { useMate } from "@/stores/mate";
import { Popup } from "vant";
import { computed, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { startChatSession } from "../chatUtils";
import {
  ChatSceneCharacterTarget,
  ChatSceneScoreStatusUpdate,
} from "@/types/chat";
import { MateSession } from "@/types/entity";

const props = defineProps<{
  sessionId?: number;
}>();

const chatSession = useChatSession();
const router = useRouter();
const mateStore = useMate();

const showEndingTargetPopup = ref(false);
const currentMate = computed(() =>
  currentSession.value?.mateId
    ? mateStore.mateIdMap.get(currentSession.value?.mateId)
    : null
);
const currentSession = ref<MateSession>();
const winningTarget = ref<ChatSceneCharacterTarget>();
const losingTarget = ref<ChatSceneCharacterTarget>();
const endingTarget = ref<ChatSceneCharacterTarget>();

const currentWinningScoreItem = ref<{ value: number }>();
const currentLosingScoreItem = ref<{ value: number }>();

const initPage = () => {
  currentSession.value = chatSession.sessionMap[props.sessionId!];
  for (const character of currentSession.value?.chatScene?.characterzz ?? []) {
    if (character.role === 2) {
      for (const target of character.targetzz ?? []) {
        if (target.type === ChatSceneTargetType.WINNING) {
          winningTarget.value = target;
        } else if (target.type === ChatSceneTargetType.LOSING) {
          losingTarget.value = target;
        } else if (
          target.id === currentSession.value?.chatSceneStatus?.endingTargetId
        ) {
          endingTarget.value = target;
        }
      }
    }
  }
  if (currentSession.value?.chatSceneStatus?.currentCharacterScores?.length) {
    for (const score of currentSession.value.chatSceneStatus
      .currentCharacterScores) {
      if (
        score.scoreId === winningTarget.value?.scoreId &&
        score.characterId === winningTarget.value?.targetCharacterId
      ) {
        currentWinningScoreItem.value = score;
      } else if (
        score.scoreId === losingTarget.value?.scoreId &&
        score.characterId === losingTarget.value?.targetCharacterId
      ) {
        currentLosingScoreItem.value = score;
      }
    }
  } else {
    const player = currentSession.value.chatScene?.characterzz?.find(
      (v) => v.role === 2
    );

    player?.targetzz?.forEach((target) => {
      const { defaultScore, currentScore } = target.score!;
      if (target.type === ChatSceneTargetType.WINNING) {
        currentWinningScoreItem.value = {
          value: currentScore ?? defaultScore ?? 0,
        };
      } else if (target.type === ChatSceneTargetType.LOSING) {
        currentLosingScoreItem.value = {
          value: currentScore ?? defaultScore ?? 0,
        };
      }
    });
  }
};

watch(endingTarget, (newVal, oldVal) => {
  if (!newVal || !!oldVal) return;
  showEndingTargetPopup.value = true;
});

watch(
  () => chatSession.sessionMap[props.sessionId!],
  (newVal, oldVal) => {
    // let isSame = JSON.stringify(newVal) === JSON.stringify(oldVal);
    // if (!isSame || !newVal) {
    initPage();
    // }
  },
  { immediate: true, deep: true }
);

// Event Handler
const restartGame = async () => {
  await startChatSession(
    currentSession.value!.mateId!,
    currentMate.value!.uuid!,
    true,
    currentSession.value?.chatScene?.id
  );
  showEndingTargetPopup.value = false;
};

const showTurnMorePopup = ref(false);

const currentPosition = computed(() => {
  const { characterzz, positionzz } = currentSession.value?.chatScene!;
  const { currentPositionId } = characterzz?.find((v) => v.role === 2) ?? {};
  return positionzz?.find((v) => v.id === currentPositionId);
});

const showPopover = ref(false);
</script>
<template>
  <div
    v-if="sessionId && (winningTarget || losingTarget)"
    class="flex justify-between bg-neutral rounded mx-2 p-2 text-xs items-center gap-2"
  >
    <div class="flex gap-1 flex-1 items-center">
      <div class="text-base-content/50">
        {{ $t("Chats.chatScene.edit.currentPosition") }}:
      </div>
      <div class="flex-1 flex items-center gap-1">
        <span class="line-clamp-1">{{ currentPosition?.name }}</span>
        <VanPopover v-model:show="showPopover" placement="top">
          <div class="text-xs p-2">
            {{ currentPosition?.name }} :
            {{ currentPosition?.shortDescription }}
          </div>
          <template #reference>
            <i class="iconify mdi--information-outline min-w-4 mt-0.5" />
          </template>
        </VanPopover>
      </div>
    </div>
    <div class="flex gap-2">
      <div class="flex gap-1 items-center">
        <div class="text-base-content/50 flex items-center gap-1">
          <i class="iconify mingcute--trophy-line" />
        </div>
        <div class="text-success">
          {{ currentWinningScoreItem?.value }} /
          {{ winningTarget?.targetScore }}
        </div>
      </div>
      <div class="flex gap-1 items-center">
        <div class="text-base-content/50 flex items-center gap-1">
          <i class="iconify mingcute--skull-line" />
        </div>
        <div class="text-error">
          {{ currentLosingScoreItem?.value }} /
          {{ losingTarget?.targetScore }}
        </div>
      </div>
      <div>
        <i
          class="iconify mingcute--more-1-line translate-y-0.5 p-0.5 cursor-pointer"
          @click="showTurnMorePopup = true"
        />
      </div>
    </div>
    <Popup
      v-model:show="showEndingTargetPopup"
      position="center"
      round
      class="p-8 flex flex-col border /90 gap-4"
    >
      <div class="flex flex-col gap-2 items-center">
        <i
          v-if="endingTarget?.type === ChatSceneTargetType.WINNING"
          class="iconify mingcute--trophy-line text-warning text-xl"
        />
        <i v-else class="iconify mingcute--skull-line text-error text-xl" />
        <div
          class="text-lg font-bold"
          :class="
            endingTarget?.type === ChatSceneTargetType.WINNING
              ? 'text-warning'
              : 'text-error'
          "
        >
          <template v-if="endingTarget?.type === ChatSceneTargetType.WINNING">
            {{ $t("Chats.targetWin") }}
          </template>
          <template v-else>
            {{ $t("Chats.targetLose") }}
          </template>
        </div>
        <div
          class="text-center text-base"
          v-if="endingTarget?.endingScript"
          :class="
            endingTarget?.type === ChatSceneTargetType.WINNING
              ? 'text-warning'
              : 'text-error'
          "
        >
          {{ endingTarget?.endingScript }}
        </div>
        <div class="text-center text-base">
          {{
            $t("Chats.targetReach", {
              targetName: endingTarget?.score?.name,
              targetScore: endingTarget?.targetScore,
            })
          }}
        </div>
      </div>
      <div class="flex justify-end gap-2">
        <button
          v-if="endingTarget?.type === ChatSceneTargetType.WINNING"
          class="btn btn-sm"
          @click="
            router.push({ name: 'home' });
            showEndingTargetPopup = false;
          "
        >
          {{ $t("Chats.pickNewMate") }}
        </button>
        <button v-else class="btn btn-sm" @click="restartGame">
          {{ $t("Chats.restartGame") }}
        </button>
        <button
          class="btn btn-primary btn-sm"
          @click="showEndingTargetPopup = false"
        >
          {{ $t("Chats.continue") }}
        </button>
      </div>
    </Popup>

    <ModalTurnMore
      v-if="currentSession"
      v-model:show="showTurnMorePopup"
      :mate-session="currentSession"
    />
  </div>
</template>
